import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { 
  Phone, 
  Mail,
  ShoppingCart,
  Settings,
  Shield,
  Cloud,
  Printer,
  Apple
} from 'lucide-react';

const serviceGroups = [
  {
    icon: ShoppingCart,
    title: 'Sales and Procurement',
    description: 'Workstations, laptops, servers, storage, UPS, peripherals, licensing, and warranty coordination.'
  },
  {
    icon: Settings,
    title: 'Managed IT Operations',
    description: 'Remote-first support with escalation when needed, monitoring and patching, onboarding and offboarding, standards, and documentation.'
  },
  {
    icon: Shield,
    title: 'Security and Continuity',
    description: 'Endpoint protection, email security, identity controls, backup strategy, and restore readiness validation.'
  },
  {
    icon: Cloud,
    title: 'Microsoft, Cloud, Voice, and Automation',
    description: 'Microsoft 365 administration, Azure setup, VoIP systems, and automation projects with clear deliverables.'
  },
  {
    icon: Printer,
    title: 'Xerox',
    description: 'NPM supports, services, and sells Xerox small and mid-size business products, including supplies and extended service contracts.'
  },
  {
    icon: Apple,
    title: 'Apple and Microsoft',
    description: 'NPM began as an Apple value-added reseller and expanded to Windows products and services. We integrate both platforms in one environment so you get the strengths of each.'
  }
];

const engagementOptions = [
  {
    title: 'Managed IT',
    description: 'Full-service IT plus security and user support'
  },
  {
    title: 'Co-managed IT',
    description: 'Augmentation for internal IT teams'
  },
  {
    title: 'Project Services',
    description: 'Migrations, refreshes, network upgrades'
  },
  {
    title: 'Sales and Deployment',
    description: 'Procure, stage, install, and support'
  }
];

const ServicesPage = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleMediaChange = (e) => setIsMobile(!e.matches);
    mediaQuery.addEventListener('change', handleMediaChange);
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  return (
    <div className="relative overflow-hidden" data-testid="services-page">
      {/* Hero Section */}
      <section className="relative hero-gradient section-padding-hero" data-testid="services-hero">
        <div className="abstract-shape abstract-shape-1"></div>
        
        <div className="site-container relative">
          <div className="max-w-3xl prose-width">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3f3f3f] leading-[1.15] mb-5">
              Our Services
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
              One partner for sales, deployment, support, and security. NPM covers day-to-day IT operations plus projects such as refreshes, migrations, and network upgrades.
            </p>
          </div>
        </div>
      </section>

      {/* Service Groups Section */}
      <section 
        className="section-padding section-bg-wrapper mobile-bg-green" 
        data-testid="service-groups-section"
      >
        {/* Background layer */}
        {!isMobile && (
          <div 
            className="section-bg-layer"
            style={{ backgroundImage: 'url(/assets/services-bg.jpg)' }}
            aria-hidden="true"
          />
        )}
        <div className="site-container section-bg-content">
          <div className="text-center mb-10 lg:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
              Service Areas
            </h2>
            <p className="text-lg lg:text-xl text-black max-w-2xl mx-auto">
              Comprehensive IT solutions for your business
            </p>
          </div>
          <div className="card-grid-services">
            {serviceGroups.map((group, index) => (
              <div key={index} className="npm-card service-card p-5 lg:p-6" data-testid={`service-group-${index}`}>
                <div className="npm-card-icon mb-4">
                  <group.icon className="w-6 h-6" />
                </div>
                <h3 className="npm-card-title mb-2">
                  {group.title}
                </h3>
                <p className="npm-card-desc text-sm">
                  {group.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Options Section */}
      <section className="section-padding bg-white" data-testid="engagement-section">
        <div className="site-container">
          <div className="text-center mb-10 lg:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
              Engagement Options
            </h2>
            <p className="text-base lg:text-lg text-gray-500 max-w-2xl mx-auto">
              Choose the level of support that fits your organization
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {engagementOptions.map((option, index) => (
              <div 
                key={index} 
                className="text-center p-5 rounded-xl border border-gray-200 bg-gray-50/50"
                data-testid={`engagement-${index}`}
              >
                <h3 className="text-base font-semibold text-[#3f3f3f] mb-2">
                  {option.title}
                </h3>
                <p className="text-sm text-gray-500">
                  {option.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Logo Carousel Section */}
      <section className="py-12 md:py-16 bg-white" data-testid="brand-carousel-section">
        <div className="site-container">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-[#3f3f3f] mb-2">
              The Brands Behind Our Solutions
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              <span className="md:whitespace-nowrap">Authorized dealer for leading technology brands,</span>
              <br className="md:hidden" />
              <span className="md:whitespace-nowrap"> backed by local service and support.</span>
            </p>
          </div>
        </div>
        
        {/* Logo Carousel */}
        <div className="logo-carousel-wrapper">
          <div className="logo-carousel-track">
            {/* First set of logos */}
            <div className="logo-carousel-slide">
              <img src="/assets/logos/aiphone.png" alt="Aiphone" className="logo-carousel-img" />
            </div>
            <div className="logo-carousel-slide">
              <img src="/assets/logos/alarm-com.png" alt="Alarm.com" className="logo-carousel-img" />
            </div>
            <div className="logo-carousel-slide">
              <img src="/assets/logos/bose.png" alt="Bose Professional" className="logo-carousel-img logo-enlarged" />
            </div>
            <div className="logo-carousel-slide">
              <img src="/assets/logos/epson.png" alt="Epson" className="logo-carousel-img" />
            </div>
            <div className="logo-carousel-slide">
              <img src="/assets/logos/klipsch.png" alt="Klipsch" className="logo-carousel-img logo-enlarged" />
            </div>
            {/* Duplicate set for seamless loop */}
            <div className="logo-carousel-slide">
              <img src="/assets/logos/aiphone.png" alt="Aiphone" className="logo-carousel-img" />
            </div>
            <div className="logo-carousel-slide">
              <img src="/assets/logos/alarm-com.png" alt="Alarm.com" className="logo-carousel-img" />
            </div>
            <div className="logo-carousel-slide">
              <img src="/assets/logos/bose.png" alt="Bose Professional" className="logo-carousel-img logo-enlarged" />
            </div>
            <div className="logo-carousel-slide">
              <img src="/assets/logos/epson.png" alt="Epson" className="logo-carousel-img" />
            </div>
            <div className="logo-carousel-slide">
              <img src="/assets/logos/klipsch.png" alt="Klipsch" className="logo-carousel-img logo-enlarged" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding cta-section" data-testid="services-cta">
        <div className="site-container text-center">
          <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
            Let's Discuss Your Needs
          </h2>
          <p className="text-base lg:text-lg text-gray-500 mb-6 max-w-2xl mx-auto">
            Contact us to schedule an assessment and learn how NPM can support your IT operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg" 
              asChild 
              className="bg-[#7dc242] hover:bg-[#6ba83a] hover:shadow-lg hover:-translate-y-0.5 text-white border-0 h-11 px-6 text-[0.9375rem] transition-all"
              data-testid="services-cta-phone"
            >
              <a href="tel:+17027695422">
                <Phone className="w-[18px] h-[18px] mr-2" />
                Call (702) 769-5422
              </a>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              asChild 
              className="border-2 border-gray-500 text-gray-600 hover:border-[#7dc242] hover:text-[#5a9e32] hover:bg-[#7dc242]/5 h-11 px-6 text-[0.9375rem] transition-all"
              data-testid="services-cta-email"
            >
              <a href="mailto:david@applevar.com">
                <Mail className="w-[18px] h-[18px] mr-2" />
                david@applevar.com
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
